import * as ActionTypes from './actionTypes'
import * as selectors from "./selectors"
import {getOpenProjectId} from "./selectors"
import {EntityType} from 'constants'
import actionsGenerator from '../comments/actionsGenerator'
import {createAction} from 'redux-actions'
import messageResource from '../../messageResource'
import serviceActionGenerator from '../comments/commentsActionsGenerator'
import rpcCall from '../../util/rpc/rpcCalls'
import CustomerStore from '../../store/CustomerStore'
import {copyTasksAction, setBulkOperationProgress} from '../../taskManagement/bulkOperations/actions'
import {CopySettingsTypes} from '../components/constants'
import ProjectStore from '../../store/ProjectStore'
import {addProjects} from '../../entities/actions'
import {toast} from '../../toasts/actions'
import {toggleLoadingMoire} from '../../taskManagement/common/actions'
import {getProject} from '../../entities/selectors'
import {copyToClipboard} from '../../atBrowserUtils'
import {createLinkToProject} from '../urlHelper/utils'

export const setOpenProject = createAction(ActionTypes.OPEN_PROJECT_SET)
export const setPanelOpenedFromUrl = createAction(ActionTypes.PROJECT_PANEL_OPENED_FROM_URL_SET)
export const setAllTasksAssigned = createAction(ActionTypes.ALL_TASKS_ASSIGNED_SET)
export const setCanManageProject = createAction(ActionTypes.CAN_MANAGE_SET)

export const commentsActions = actionsGenerator({
    actionTypes: ActionTypes
})

export const commentsServiceActions = serviceActionGenerator({
    actions: commentsActions,
    selectors: {
        ...selectors,
        getOpenEntityId: selectors.getOpenProjectId
    },
    handleServiceError: (projectId, error) => handleServiceError(projectId, error),
    entityType: EntityType.PROJECT
})

export const openProjectPanel = (projectId, fromUrl = false) => (dispatch, getState) => {
    const state = getState()

    const openProjectId = getOpenProjectId(state)

    if (openProjectId !== projectId) {
        dispatch(setPanelOpenedFromUrl(fromUrl))
        dispatch(setOpenProject(projectId))
    } else {
        dispatch(closeProjectPanel())
    }
}

export const closeProjectPanel = () => dispatch => {
    dispatch(setOpenProject(null))
    dispatch(setPanelOpenedFromUrl(false))
}

export const reloadComments = commentsServiceActions.reloadComments

export const updateNewCommentText = text => (dispatch, getState) => {
    const state = getState()
    const projectId = getOpenProjectId(state)
    if (text !== '') {
        dispatch(commentsActions.setNewComment({entityId: projectId, text}))
    } else {
        dispatch(commentsActions.removeNewComment(projectId))
    }
    dispatch(commentsServiceActions.saveEntityNewCommentsTextsInLocalStorage())
}

const handleServiceError = (projectId, error) => {
    if (error.getTextStatus() === 'rpc_customException') {
        switch (error.getErrorThrown().exception) {
            case 'com.actimind.actitime.services.impl.comments.NoSuchEntityException':
                alert(messageResource.getMessage('project.edit.deleted_concurrently'));
                window.location.reload();
                return true;
            case 'com.actimind.actitime.services.impl.comments.EntityNotAssignedException':
                alert(messageResource.getMessage('common.no_rights_for_operation_anymore'));
                window.location.reload();
                return true;
            case 'com.actimind.actitime.services.NoRightToEditHierarchyException':
                alert(messageResource.getMessage('common.no_rights_for_operation_anymore'));
                window.location.reload();
                return true;
            case 'com.actimind.actitime.services.impl.comments.NoSuchCommentException':
                alert(messageResource.getMessage('panel.common.comments.comment_concurrently_deleted'));
                return true;
        }
    }
    return false
}

export const copyProjectAction = (customerId, settings, onProjectCopiedCb = () => {}) => (dispatch, getState) => {
    const state = getState()
    const projectId = getOpenProjectId(state)
    const oldProject = getProject(state, projectId)
    dispatch(toggleLoadingMoire(true))
    dispatch(setBulkOperationProgress(null))
    rpcCall(
        'ProjectInfoService.copyProject',
        [
            projectId,
            customerId,
            separateProjectCopySettings(settings),
        ],
        (error) => {
            if (error.getTextStatus() === 'rpc_customException') {
                switch (error.getErrorThrown().exception) {
                    case 'com.actimind.actitime.services.NoRightToEditHierarchyException':
                        alert(messageResource.getMessage("project.error.copy.no_manage_scope_right"))
                        window.location.reload()
                        error.handle()
                        return true
                    case 'com.actimind.actitime.project.NoSuchProjectException':
                        alert(messageResource.getMessage("project.error.copy.project_deleted_concurrently"))
                        window.location.reload()
                        error.handle()
                        return true
                    case 'com.actimind.actitime.customer.NoSuchCustomerException':
                        alert(messageResource.getMessage("project.error.copy.customer_deleted_concurrently"))
                        window.location.reload()
                        error.handle()
                        return true
                    case 'com.actimind.actitime.customer.CustomerArchivedException':
                        alert(messageResource.getMessage("project.error.copy.customer_archived_concurrently"))
                        window.location.reload()
                        error.handle()
                        return true
                    case 'com.actimind.actitime.project.ProjectIsNotAssignedException':
                        alert(messageResource.getMessage("project.error.copy.project_not_assigned"))
                        window.location.reload()
                        error.handle()
                        return true
                    case 'com.actimind.actitime.customer.CustomerUnavailableException':
                        alert(messageResource.getMessage("project.error.copy.customer_not_assigned"))
                        window.location.reload()
                        error.handle()
                        return true
                    case 'com.actimind.actitime.project.ProjectExistsException':
                        alert(messageResource.getMessage("project.error.project_with_same_name_exists"))
                        dispatch(toggleLoadingMoire(false))
                        error.handle()
                        return true
                }
            }
            return false
        }
    ).then((data) => {
        const project = data.project
        ProjectStore.addFirst(project)
        dispatch(addProjects([project]))
        if (data.taskIds.length > 0) {
            dispatch(copyTasksAction(
                data.taskIds,
                project.id,
                settings,
                data.operationId,
            ))
        }
        dispatch(toggleLoadingMoire(false))

        const customerName = CustomerStore.get(project.customerId).name
        dispatch(toast(messageResource.getMessage("copy.project.toast", [project.name, customerName])))

        if (oldProject.name !== project.name)
            dispatch(toast(messageResource.getMessage('copy.project.duplicate_name.toast')))

        onProjectCopiedCb()
    }).catch((error) => {
        if (!error.isHandled()) console.error(error)
    })
}

export const separateProjectCopySettings = (settings) => {
    return {
        copyDescription: !!settings[CopySettingsTypes.COPY_PROJECT_DESCRIPTION],
        copyAssignedUsers: !!settings[CopySettingsTypes.COPY_ASSIGNED_USERS],
        copyManagers: !!settings[CopySettingsTypes.COPY_MANAGERS],
    }
}

export const separateTaskCopySettings = (settings) => {
    return {
        copyWorkflowStatus: !!settings[CopySettingsTypes.COPY_TASK_STATUS],
        copyTypeOfWork: !!settings[CopySettingsTypes.COPY_TASK_TYPE_OF_WORK],
        copyEstimate: !!settings[CopySettingsTypes.COPY_TASK_ESTIMATE],
        copyDescription: settings[CopySettingsTypes.COPY_TASK_DESCRIPTION],
        copyAssignedUsers: !!settings[CopySettingsTypes.COPY_ASSIGNED_USERS],
        copyManagers: !!settings[CopySettingsTypes.COPY_MANAGERS],
    }
}

export const copyLinkTo = () => (dispatch, getState) => {
    const projectId = getOpenProjectId(getState())
    copyToClipboard(createLinkToProject(projectId), () => {
        dispatch(toast(messageResource.getMessage('panel.common.link_copied')))
    })
}



// WEBPACK FOOTER //
// ./web/js/panels/project/actions.js